import { useFragment } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';

import { getMemberId } from '@trello/authentication';
import { client } from '@trello/graphql';

import type { Account } from './Planner.types';
import { TrelloPlannerAccountByIdFieldsFragmentDoc } from './TrelloPlannerAccountByIdFieldsFragment.generated';
import { TrelloPlannerMemberAccountFragmentDoc } from './TrelloPlannerMemberAccountFragment.generated';

export const usePlannerAccounts = () => {
  const memberNodeId = getMemberId({ returnAri: true });

  const { data, complete: isAccountDataComplete } = useFragment({
    fragment: TrelloPlannerMemberAccountFragmentDoc,
    fragmentName: 'TrelloPlannerMemberAccount',
    from: { __typename: 'TrelloMember', id: memberNodeId },
  });

  const {
    accountsWithScopeIssues,
    allAccounts,
    expiredAccounts,
    problematicAccounts,
    validAccounts,
  } = useMemo(() => {
    const all: Account[] = [];
    const valid: Account[] = [];
    const problematic: Account[] = [];
    const expired: Account[] = [];
    const withScopeIssues: Account[] = [];

    const accounts = data?.planner?.accounts?.edges;
    if (accounts) {
      for (const account of accounts) {
        const accountNode = account.node;
        if (!accountNode?.id) {
          continue;
        }

        // Use readFragment to validate that all expected fields are present
        // This ensures the account data is fully loaded in the GraphQL cache
        const validatedAccount = client.readFragment({
          id: client.cache.identify({
            __typename: 'TrelloPlannerCalendarAccount',
            id: accountNode.id,
          }),
          fragment: TrelloPlannerAccountByIdFieldsFragmentDoc,
          optimistic: true,
        });

        if (!validatedAccount) {
          continue;
        }

        all.push(validatedAccount);

        if (validatedAccount.isExpired) {
          problematic.push(validatedAccount);
          expired.push(validatedAccount);
        } else if (!validatedAccount.hasRequiredScopes) {
          problematic.push(validatedAccount);
          withScopeIssues.push(validatedAccount);
        } else {
          valid.push(validatedAccount);
        }
      }
    }

    return {
      accountsWithScopeIssues: withScopeIssues,
      allAccounts: all,
      expiredAccounts: expired,
      problematicAccounts: problematic,
      validAccounts: valid,
    };
  }, [data?.planner?.accounts?.edges]);

  /**
   * Returns the primary account ID for the user's planner accounts.
   *
   * Uses the primaryAccountId field from the backend if available,
   * otherwise finds the first valid account (has required scopes and not expired).
   *
   * @returns The ID of the primary planner account, or undefined if no valid account exists
   */
  const primaryAccountId = useMemo(
    () =>
      data?.planner?.primaryAccountId ||
      data?.planner?.accounts?.edges?.find(
        (edge) => edge?.node?.hasRequiredScopes && !edge?.node?.isExpired,
      )?.node?.id ||
      undefined,
    [data?.planner?.accounts?.edges, data?.planner?.primaryAccountId],
  );

  const primaryAccount = useMemo(
    () =>
      data?.planner?.accounts?.edges?.find(
        (edge) => edge?.node?.id === primaryAccountId,
      ),
    [data?.planner?.accounts?.edges, primaryAccountId],
  );

  const defaultCalendar = useMemo(
    () => data?.planner?.primaryCalendar,
    [data?.planner?.primaryCalendar],
  );

  /**
   * Returns the enabled calendars for a specific account ID.
   *
   * @param accountId - The account ID to get enabled calendars for
   * @returns Array of enabled calendars for the account
   */
  const getEnabledCalendarsForAccount = useCallback(
    (accountId: string) => {
      // Find the account in validAccounts and return its enabled calendars
      const account = validAccounts.find((acc) => acc?.id === accountId);
      const calendars = account?.enabledCalendars?.edges
        ?.map((edge) => edge?.node)
        .filter(Boolean);

      return calendars && calendars.length > 0 ? calendars : undefined;
    },
    [validAccounts],
  );

  /**
   * Returns the account with the given ID.
   *
   * Note: This only searches validAccounts (accounts with required scopes that aren't expired).
   * If you need to find problematic accounts, search allAccounts separately.
   *
   * @param accountId - The ID of the account to get
   * @returns The account if found in validAccounts, undefined otherwise
   */
  const getAccountById = useCallback(
    (accountId: string): Account | undefined =>
      validAccounts.find((acc) => acc?.id === accountId),
    [validAccounts],
  );

  /**
   * Returns the provider calendars for a specific account ID.
   *
   * @param accountId - The account ID to get provider calendars for
   * @returns Array of provider calendars for the account
   */
  const getProviderCalendarsForAccount = useCallback(
    (accountId: string) => {
      const account = getAccountById(accountId);
      const calendars = account?.providerCalendars?.edges
        ?.map((edge) => edge?.node)
        .filter(Boolean);
      return calendars?.length ? calendars : undefined;
    },
    [getAccountById],
  );

  /**
   * Returns the provider calendar for a given calendar ID. Searches
   * through all valid accounts to find the calendar.
   *
   * @param calendarId - The ID of the calendar to get
   */
  const getProviderCalendarById = useCallback(
    (calendarId: string) => {
      const calendar = validAccounts
        .map((acc) => getProviderCalendarsForAccount(acc?.id))
        .flat()
        .find((cal) => cal?.id === calendarId);
      return calendar ? calendar! : undefined;
    },
    [validAccounts, getProviderCalendarsForAccount],
  );

  /**
   * Returns the valid account that contains the provider calendar with the
   * given ID.
   *
   * @param calendarId - The ID of the calendar to get the account for
   */
  const getAccountForProviderCalendarId = useCallback(
    (calendarId: string) => {
      const calendar = getProviderCalendarById(calendarId);
      return calendar?.providerAccountId
        ? getAccountById(calendar.providerAccountId)
        : undefined;
    },
    [getAccountById, getProviderCalendarById],
  );

  /**
   * Returns the primary provider calendar for a given account ID.
   *
   * @param accountId - The ID of the account to get the primary provider calendar for
   */
  const getPrimaryProviderCalendarForAccount = useCallback(
    (accountId: string) => {
      const account = getProviderCalendarsForAccount(accountId);
      const calendar = account?.find((cal) => cal?.isPrimary);
      return calendar ? calendar! : undefined;
    },
    [getProviderCalendarsForAccount],
  );

  /**
   * Returns all enabled calendars for all valid accounts that are
   * not read-only.
   *
   * @returns Array<{ calendar: TrelloPlannerCalendar, account: TrelloPlannerCalendarAccount }>
   */
  const getAllEnabledWritableCalendars = useCallback(() => {
    return validAccounts
      .map((account) => {
        const calendars = getEnabledCalendarsForAccount(account?.id) ?? [];
        return calendars
          .filter((calendar) => {
            const providerCalendar = getProviderCalendarById(
              calendar?.providerCalendarId ?? '',
            );
            return (
              !!calendar && !!providerCalendar && !providerCalendar?.readOnly
            );
          })
          .map((calendar) => ({
            calendar,
            account,
          }));
      })
      .flat();
  }, [validAccounts, getEnabledCalendarsForAccount, getProviderCalendarById]);

  const hasLoadedEveryAccountsEnabledCalendars = useMemo(
    () =>
      validAccounts.length > 0 &&
      validAccounts.every(
        (account) => account?.enabledCalendars?.pageInfo?.hasNextPage === false,
      ),
    [validAccounts],
  );

  const [hasFinishedCalendarLoad, setHasFinishedCalendarLoad] = useState(false);
  if (hasLoadedEveryAccountsEnabledCalendars && !hasFinishedCalendarLoad) {
    setHasFinishedCalendarLoad(true);
  }

  const hasReachedAccountLimit = useMemo(() => {
    return allAccounts.length >= 10;
  }, [allAccounts]);

  /**
   * Accounts filtered to only include those that have an enabled primary calendar.
   * This is useful for components that need to show account options
   * where only accounts with enabled primary calendars are available.
   */
  const accountsWithEnabledPrimaryCalendar = useMemo(() => {
    return validAccounts.filter((account) => {
      if (!account?.id) {
        return false;
      }
      const enabledCalendars = getEnabledCalendarsForAccount(account.id);
      const primaryCalendar = enabledCalendars?.find((cal) => cal?.isPrimary);
      return !!primaryCalendar?.id;
    });
  }, [validAccounts, getEnabledCalendarsForAccount]);

  /**
   * Sorts a list of accounts so the primary account is first, then
   * alphabetically by display name. Also ensures, for type safety, that null
   * accounts are filtered out.
   *
   * @param accounts - The accounts to sort and sanitize
   * @returns The sorted and sanitized accounts
   */
  const sortAndSanitizeAccounts = useCallback(
    (accounts: (Account | null)[]) => {
      return accounts
        .filter((account): account is Account => !!account)
        .sort((a, b) => {
          if (a?.id === primaryAccountId) {
            return -1;
          } else if (b?.id === primaryAccountId) {
            return 1;
          }

          const isAValid = validAccounts.find((acc) => acc?.id === a?.id);
          const isBValid = validAccounts.find((acc) => acc?.id === b?.id);

          if (isAValid && !isBValid) {
            return -1;
          } else if (!isAValid && isBValid) {
            return 1;
          }

          return a?.displayName?.localeCompare(b?.displayName ?? '') ?? 1;
        });
    },
    [primaryAccountId, validAccounts],
  );

  const hasOneAccount = useMemo(() => {
    return allAccounts.length === 1;
  }, [allAccounts]);

  return {
    accountsWithEnabledPrimaryCalendar,
    accountsWithScopeIssues,
    allAccounts,
    expiredAccounts,
    getEnabledCalendarsForAccount,
    getProviderCalendarsForAccount,
    getAllEnabledWritableCalendars,
    hasOneAccount,
    getAccountById,
    getProviderCalendarById,
    getPrimaryProviderCalendarForAccount,
    getAccountForProviderCalendarId,
    hasReachedAccountLimit,
    isAccountDataComplete,
    isLoadingAllAccountsEnabledCalendars: !hasFinishedCalendarLoad,
    primaryAccount,
    primaryAccountId,
    defaultCalendar,
    defaultCalendarId: defaultCalendar?.id ?? null,
    problematicAccounts,
    validAccounts,
    sortAndSanitizeAccounts,
  };
};
